import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileApi } from '../lib/api';
import { getPrivateKey } from '../lib/crypto';
import { Button } from '../components/ui/button';
import AppShell from '../components/AppShell';
import { PageHeader } from '../components/PageHeader';
import MonthYearPicker from '../components/MonthYearPicker';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { AvatarCropper } from '../components/AvatarCropper';
import { PasswordUnlockDialog } from '../components/PasswordUnlockDialog';
import { RecoveryCodeDialog } from '../components/RecoveryCodeDialog';
import { authApi } from '../lib/api';
import { SharedAccessCard } from '../components/SharedAccessCard';
import { StatusPill } from '../components/StatusPills';
import { normaliseName } from '../lib/textCase';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  X,
  Trash2,
  Loader2,
  Camera,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const FIXED_PRONOUNS = ['he/him', 'she/her', 'they/them', 'prefer not to say'];

export const Profile = () => {  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loadUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState(location.state?.openTab || 'personal');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // ID Documents state
  const [uploadingId, setUploadingId] = useState(false);
  const [idSharingPermission, setIdSharingPermission] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [idDocNames, setIdDocNames] = useState({});
  const [reencrypting, setReencrypting] = useState(false);
  const idFileInputRef = useRef(null);

  // Privacy state
  const [contributeStats, setContributeStats] = useState(true);
  const [savingContributeStats, setSavingContributeStats] = useState(false);
  const [contributeDemographics, setContributeDemographics] = useState(false);
  const [savingContributeDemographics, setSavingContributeDemographics] = useState(false);
  const [demographicsOptions, setDemographicsOptions] = useState({ ethnicity: [], gender: [], pronouns: [], disability: [] });
  const [demographicsForm, setDemographicsForm] = useState({ ethnicity: '', disability: '' });
  const [savingDemographicField, setSavingDemographicField] = useState(null);
  
  // Avatar state
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);

  // Unlock dialog state (for re-deriving the zero-knowledge private key
  // when sessionStorage was cleared, e.g. after a hard refresh)
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  // Recovery code: password prompt -> freshly issued code shown once
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(Boolean(location.state?.openRecovery));
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [issuedRecoveryCode, setIssuedRecoveryCode] = useState(null);

  const handleGenerateRecoveryCode = async (e) => {
    e?.preventDefault();
    if (!recoveryPassword) return;
    setRecoveryBusy(true);
    setRecoveryError('');
    try {
      const { data } = await authApi.generateRecoveryCode(recoveryPassword);
      setRecoveryPassword('');
      setShowRecoveryPrompt(false);
      setIssuedRecoveryCode(data.recovery_code);
    } catch (err) {
      setRecoveryError(err?.response?.status === 401
        ? 'Incorrect password. Please try again.'
        : (err?.response?.data?.detail || 'Could not generate a recovery code'));
    } finally {
      setRecoveryBusy(false);
    }
  };
  const [pendingIdPermission, setPendingIdPermission] = useState(null);
  const [pendingExport, setPendingExport] = useState(false);
  
  // Profile form state
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [pronounsCustom, setPronounsCustom] = useState('');
  const [previousNames, setPreviousNames] = useState([]);
  const [newPreviousName, setNewPreviousName] = useState('');
  const [currentAddress, setCurrentAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    is_current: true
  });
  const [addressHistory, setAddressHistory] = useState([]);

  useEffect(() => {
    if (user) {
      setDateOfBirth(user.date_of_birth || '');
      setGender(user.gender || '');
      if (user.pronouns && !FIXED_PRONOUNS.includes(user.pronouns)) {
        setPronouns('self-describe');
        setPronounsCustom(user.pronouns);
      } else {
        setPronouns(user.pronouns || '');
        setPronounsCustom('');
      }
      setPreviousNames(user.previous_names || []);
      if (user.current_address) {
        setCurrentAddress(user.current_address);
      }
      // Load previous addresses (may come from previous_addresses or legacy address_history)
      setAddressHistory(user.previous_addresses || user.address_history || []);
      setIdSharingPermission(user.id_sharing_permission || false);
      setContributeStats(user.contribute_anonymised_stats !== false);
      setContributeDemographics(user.contribute_demographic_stats === true);
      setDemographicsForm({
        ethnicity: user.demographics?.ethnicity || '',
        disability: user.demographics?.disability || '',
      });
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    profileApi.demographicsOptions()
      .then((res) => !cancelled && setDemographicsOptions(res.data))
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleToggleContributeStats = async (checked) => {
    setContributeStats(checked);
    setSavingContributeStats(true);
    try {
      await profileApi.update({ contribute_anonymised_stats: checked });
      await loadUser();
      toast.success(checked ? 'Thanks — you\'re contributing anonymised stats.' : 'Contribution turned off.');
    } catch {
      setContributeStats(!checked);
      toast.error('The setting could not be changed.');
    } finally {
      setSavingContributeStats(false);
    }
  };

  const handleToggleContributeDemographics = async (checked) => {
    setContributeDemographics(checked);
    setSavingContributeDemographics(true);
    try {
      await profileApi.update({ contribute_demographic_stats: checked });
      await loadUser();
      toast.success(checked ? 'Thanks — demographic details will be included.' : 'Demographic contribution turned off.');
    } catch {
      setContributeDemographics(!checked);
      toast.error('The setting could not be changed.');
    } finally {
      setSavingContributeDemographics(false);
    }
  };

  const handleDemographicFieldChange = async (field, value) => {
    const next = { ...demographicsForm, [field]: value };
    setDemographicsForm(next);
    setSavingDemographicField(field);
    try {
      await profileApi.update({ demographics: next });
      await loadUser();
    } catch {
      toast.error('Could not save that.');
    } finally {
      setSavingDemographicField(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Signed out.');
  };

  const addPreviousName = () => {
    const trimmed = newPreviousName.trim();
    if (trimmed) {
      const cleaned = normaliseName(trimmed) || trimmed;
      setPreviousNames([...previousNames, cleaned]);
      setNewPreviousName('');
    }
  };

  const removePreviousName = (index) => {
    setPreviousNames(previousNames.filter((_, i) => i !== index));
  };

  const addAddressToHistory = () => {
    setAddressHistory([...addressHistory, {
      id: crypto.randomUUID(),
      address: '',
      start_date: '',
      end_date: ''
    }]);
  };

  const updateAddressHistory = (index, field, value) => {
    const updated = [...addressHistory];
    updated[index] = { ...updated[index], [field]: value };
    setAddressHistory(updated);
  };

  const removeAddressHistory = (index) => {
    setAddressHistory(addressHistory.filter((_, i) => i !== index));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Filter out empty addresses - check for either 'address' (single field) or 'address_line1' (legacy)
      const validAddresses = addressHistory.filter(addr => 
        (addr.address && addr.address.trim()) || (addr.address_line1 && addr.address_line1.trim())
      );
      
      const pronounsToSave = pronouns === 'self-describe' ? pronounsCustom.trim() : pronouns;
      await profileApi.update({
        date_of_birth: dateOfBirth || null,
        gender: gender || null,
        pronouns: pronounsToSave || null,
        previous_names: previousNames,
        current_address: currentAddress.address_line1 ? currentAddress : null,
        previous_addresses: validAddresses
      });
      await loadUser();
      setEditing(false);
      toast.success('Saved.');
    } catch {
      toast.error('Your profile could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const privateKey = getPrivateKey();
      // If the user has encrypted documents but no session key, prompt for
      // password — same UX as the ID sharing toggle.
      const hasEncryptedDocs = (user?.id_documents?.length || 0) > 0;
      if (hasEncryptedDocs && !privateKey) {
        setPendingExport(true);
        setShowUnlockDialog(true);
        return;
      }
      const response = await profileApi.export(privateKey);
      const filename = (response.headers?.['content-disposition'] || '')
        .match(/filename="?([^";]+)"?/)?.[1]
        || `precious-data-export-${new Date().toISOString().split('T')[0]}.zip`;
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export saved to your downloads.');
    } catch {
      toast.error('The export could not be created.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    
    setDeleting(true);
    try {
      await profileApi.deleteAccount();
      logout();
      navigate('/');
      toast.success('Account deleted.');
    } catch {
      toast.error('Your account could not be deleted.');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // ID Document handlers
  const handleIdFileSelect = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('That file is over 10 MB.');
      return;
    }
    
    setUploadingId(true);
    try {
      await profileApi.uploadIdDocument(file, documentType);
      await loadUser();
      toast.success('Document uploaded.');
    } catch {
      toast.error('The document could not be uploaded.');
    } finally {
      setUploadingId(false);
      if (idFileInputRef.current) {
        idFileInputRef.current.value = '';
      }
    }
  };

  // Avatar handlers
  const handleAvatarSave = async (croppedBlob) => {
    try {
      const file = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
      await profileApi.uploadAvatar(file);
      await loadUser();
      toast.success('Picture updated.');
    } catch (error) {
      toast.error('Your picture could not be updated.');
      throw error; // Re-throw to let cropper know it failed
    } finally {
    }
  };

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    const name = user?.full_name || user?.email || '';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleDeleteIdDocument = async (docId) => {
    setDeletingDocId(docId);
    try {
      await profileApi.deleteIdDocument(docId);
      await loadUser();
      toast.success('Document deleted.');
    } catch {
      toast.error('The document could not be deleted.');
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleSaveIdPermission = async (checked, overrideKey = null) => {
    const hasDocuments = user?.id_documents?.length > 0;
    
    // If enabling sharing and there are documents, we need to re-encrypt them with system key
    // This requires the user's private key to decrypt first
    if (checked && hasDocuments) {
      const privateKey = overrideKey || getPrivateKey();
      if (!privateKey) {
        // Key was cleared from sessionStorage (e.g. hard refresh).
        // Prompt the user to re-enter their password to re-derive it.
        setPendingIdPermission(checked);
        setShowUnlockDialog(true);
        return;
      }
      
      setReencrypting(true);
      try {
        await profileApi.reencryptIdDocuments(true, privateKey);
        setIdSharingPermission(true);
        await loadUser();
        toast.success('ID sharing on. Your ID is sent automatically when an organisation asks for it.');
      } catch (error) {
        console.error('Re-encryption failed:', error);
        toast.error('ID sharing could not be turned on. Try again.');
      } finally {
        setReencrypting(false);
      }
      return;
    }
    
    // If disabling sharing and there are documents, re-encrypt with user key (zero-knowledge)
    if (!checked && hasDocuments) {
      setReencrypting(true);
      try {
        await profileApi.reencryptIdDocuments(false);
        setIdSharingPermission(false);
        await loadUser();
        toast.success('ID sharing off. You approve each ID request yourself.');
      } catch (error) {
        console.error('Re-encryption failed:', error);
        toast.error('ID sharing could not be turned off. Try again.');
      } finally {
        setReencrypting(false);
      }
      return;
    }
    
    // No documents, just update the permission
    setIdSharingPermission(checked);
    try {
      await profileApi.update({ id_sharing_permission: checked });
      await loadUser();
      toast.success(checked ? 'ID sharing on.' : 'ID sharing off.');
    } catch {
      toast.error('The setting could not be changed.');
    }
  };

  // Called by the unlock dialog after the private key is restored to sessionStorage
  const handleUnlocked = (privateKey) => {
    if (pendingIdPermission !== null) {
      const checked = pendingIdPermission;
      setPendingIdPermission(null);
      handleSaveIdPermission(checked, privateKey);
      return;
    }
    if (pendingExport) {
      setPendingExport(false);
      // Re-trigger export now that we have the key
      handleExportData();
    }
  };


  // ID-document filenames are sealed; open them whenever the list changes.
  const idDocIds = (user?.id_documents || []).map((d) => d.id).join(',');
  useEffect(() => {
    if (!idDocIds) return;
    let active = true;
    profileApi.idDocumentNames().then((names) => { if (active) setIdDocNames(names); });
    return () => { active = false; };
  }, [idDocIds]);

  const ID_DOC_KIND = {
    proof_of_id: 'Proof of ID',
    proof_of_address: 'Proof of address',
    name_change_evidence: 'Name change evidence',
  };
  const idDocName = (doc) => idDocNames[doc.id] || doc.original_filename || ID_DOC_KIND[doc.type] || 'Document';

  const idDocRow = (doc, testId) => (
    <div key={doc.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-brand-wash">
      <div className="min-w-0">
        <p className="text-[15px] font-medium text-brand-ink truncate">{idDocName(doc)}</p>
        <p className="text-sm text-brand-ink/80">Uploaded{doc.uploaded_at ? ` ${formatDate(doc.uploaded_at)}` : ''}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleDeleteIdDocument(doc.id)}
        disabled={deletingDocId === doc.id || (deletingDocId && testId !== 'name-change')}
        className="h-9 w-9 text-brand-ink hover:text-breach-ink flex-shrink-0"
        aria-label={`Delete ${idDocName(doc)}`}
      >
        {deletingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      </Button>
    </div>
  );

  const proofOfId = user?.id_documents?.find(d => d.type === 'proof_of_id');
  const proofOfAddress = user?.id_documents?.find(d => d.type === 'proof_of_address');
  const nameChangeDocs = user?.id_documents?.filter(d => d.type === 'name_change_evidence') || [];

  return (
    <AppShell width="narrow">
      <main>
        <PageHeader
          title="Profile"
          lede="Your details, your ID documents and who can open your records. Requests are filled in from here."
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList aria-label="Profile sections">
            <TabsTrigger value="personal" data-testid="profile-tab-personal">Personal</TabsTrigger>
            <TabsTrigger value="security" data-testid="profile-tab-security">Security</TabsTrigger>
            <TabsTrigger value="privacy" data-testid="profile-tab-privacy">Privacy</TabsTrigger>
            <TabsTrigger value="account" data-testid="profile-tab-account">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="space-y-4">
        {/* Profile Info */}
        <Card className="bg-ground mb-4">
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="font-heading text-[19px] font-semibold tracking-[-0.02em]">About you</CardTitle>
              <CardDescription className="text-ink-55">
                These details help organisations find your records.
              </CardDescription>
            </div>
            {!editing && (
              <Button
                variant="secondary"
                size="sm"
                className="bg-surface hover:bg-inset flex-shrink-0"
                onClick={() => setEditing(true)}
                data-testid="edit-profile-btn"
              >
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info with Avatar */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-surface">
              <div className="relative group">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={user?.avatar_url} alt={user?.full_name} />
                  <AvatarFallback className="text-xl font-heading font-bold bg-avatar text-ink">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => setShowAvatarCropper(true)}
                  className="absolute inset-0 bg-ink/50 rounded-full opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-state flex items-center justify-center"
                  aria-label="Change your picture"
                >
                  <Camera className="w-5 h-5 text-white" />
                </button>
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">{user?.full_name}</h3>
                <p className="text-sm text-ink-55">{user?.email}</p>
                <button
                  type="button"
                  onClick={() => setShowAvatarCropper(true)}
                  className="text-sm font-medium text-brand hover:underline mt-1"
                >
                  {user?.avatar_url ? 'Change picture' : 'Add a picture'}
                </button>
              </div>
            </div>

            {editing ? (
              <div className="space-y-6">
                {/* Date of Birth */}
                <div className="space-y-2">
                  <Label className="text-[15px] font-semibold">When were you born?</Label>
                  <Input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="max-w-xs bg-surface border-surface"
                    data-testid="dob-input"
                  />
                </div>

                {/* Gender + Pronouns */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[15px] font-semibold">Gender</Label>
                    <p className="text-xs text-ink-55">Used to personalise your SAR letters, and in anonymised stats if you opt in under Privacy.</p>
                    <Select value={gender || undefined} onValueChange={setGender}>
                      <SelectTrigger className="bg-surface border-surface" data-testid="gender-trigger">
                        <SelectValue placeholder="Not answered" />
                      </SelectTrigger>
                      <SelectContent>
                        {demographicsOptions.gender.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[15px] font-semibold">Pronouns</Label>
                    <p className="text-xs text-ink-55">So we can address you correctly in the SAR letters we write on your behalf.</p>
                    <Select value={pronouns || undefined} onValueChange={setPronouns}>
                      <SelectTrigger className="bg-surface border-surface" data-testid="pronouns-trigger">
                        <SelectValue placeholder="Not answered" />
                      </SelectTrigger>
                      <SelectContent>
                        {demographicsOptions.pronouns.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                        <SelectItem value="self-describe">Self-describe</SelectItem>
                      </SelectContent>
                    </Select>
                    {pronouns === 'self-describe' && (
                      <Input
                        value={pronounsCustom}
                        onChange={(e) => setPronounsCustom(e.target.value)}
                        placeholder="Enter your own pronouns"
                        maxLength={30}
                        className="bg-surface border-surface"
                        data-testid="pronouns-custom-input"
                      />
                    )}
                  </div>
                </div>

                {/* Previous Names */}
                <div className="space-y-2">
                  <Label className="text-[15px] font-semibold">Have you been known by any other names?</Label>
                  <p className="text-sm text-ink-55">A name you were given at birth, a name you changed to, or a family name from a placement.</p>
                  {previousNames.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {previousNames.map((name, index) => (
                        <span key={index} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-inset text-ink-70 rounded-full text-sm">
                          {name}
                          <button
                            type="button"
                            onClick={() => removePreviousName(index)}
                            className="rounded-full p-0.5 hover:bg-rule transition-colors duration-state"
                            aria-label={`Remove ${name}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={newPreviousName}
                      onChange={(e) => setNewPreviousName(e.target.value)}
                      onBlur={(e) => setNewPreviousName(normaliseName(e.target.value) || e.target.value)}
                      placeholder="Add a previous name"
                      className="bg-surface border-surface"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPreviousName())}
                    />
                    <Button type="button" variant="secondary" className="bg-surface hover:bg-inset" onClick={addPreviousName}>
                      Add
                    </Button>
                  </div>
                </div>

                {/* Current Address */}
                <div className="space-y-3">
                  <Label className="text-[15px] font-semibold">Where do you live now?</Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input
                      value={currentAddress.address_line1}
                      onChange={(e) => setCurrentAddress({...currentAddress, address_line1: e.target.value})}
                      placeholder="Address line 1"
                      aria-label="Address line 1"
                      className="bg-surface border-surface"
                    />
                    <Input
                      value={currentAddress.address_line2}
                      onChange={(e) => setCurrentAddress({...currentAddress, address_line2: e.target.value})}
                      placeholder="Address line 2 (optional)"
                      aria-label="Address line 2"
                      className="bg-surface border-surface"
                    />
                    <Input
                      value={currentAddress.city}
                      onChange={(e) => setCurrentAddress({...currentAddress, city: e.target.value})}
                      onBlur={(e) => setCurrentAddress({...currentAddress, city: normaliseName(e.target.value) || e.target.value})}
                      placeholder="Town or city"
                      aria-label="Town or city"
                      className="bg-surface border-surface"
                    />
                    <Input
                      value={currentAddress.postcode}
                      onChange={(e) => setCurrentAddress({...currentAddress, postcode: e.target.value})}
                      placeholder="Postcode"
                      aria-label="Postcode"
                      className="bg-surface border-surface"
                    />
                  </div>
                </div>

                {/* Address History */}
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Label className="text-[15px] font-semibold">Where have you lived before?</Label>
                      <p className="text-sm text-ink-55">Old addresses help organisations find older records.</p>
                    </div>
                    <Button type="button" variant="secondary" size="sm" className="bg-surface hover:bg-inset flex-shrink-0" onClick={addAddressToHistory}>
                      Add an address
                    </Button>
                  </div>

                  {addressHistory.map((addr, index) => (
                    <div key={addr.id || index} className="p-4 bg-surface rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="pd-eyebrow">Previous address {index + 1}</span>
                          {addr.auto_added && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-help">
                                    <StatusPill tone="neutral" size="sm">Added from a placement</StatusPill>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-sm">
                                    This address came from your placement details at{' '}
                                    <strong>{addr.source_org_name || 'an organisation'}</strong>.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeAddressHistory(index)} className="h-9 w-9" aria-label={`Remove previous address ${index + 1}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Single address field for all previous addresses */}
                      <div className="space-y-3">
                        <Input
                          value={addr.address || addr.address_line1 || ''}
                          onChange={(e) => updateAddressHistory(index, 'address', e.target.value)}
                          placeholder="Full address, for example 123 High Street, Manchester, M1 1AA"
                          aria-label="Full address"
                        />
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-[15px] font-semibold mb-1 block">When did you move in?</Label>
                            <MonthYearPicker
                              value={addr.start_date || ''}
                              onChange={(v) => updateAddressHistory(index, 'start_date', v)}
                              ariaLabel="Address start date"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-[15px] font-semibold mb-1 block">When did you leave?</Label>
                            <MonthYearPicker
                              value={addr.end_date || ''}
                              onChange={(v) => updateAddressHistory(index, 'end_date', v)}
                              ariaLabel="Address end date"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    data-testid="save-profile-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Saving" /> : 'Save'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-surface">
                  <p className="pd-eyebrow mb-1">Date of birth</p>
                  <p className="pd-mono text-ink">{user?.date_of_birth || 'Not set'}</p>
                </div>
                <div className="p-4 rounded-lg bg-surface">
                  <p className="pd-eyebrow mb-1">Gender</p>
                  <p className="text-[15px] font-medium text-ink">{user?.gender || 'Not set'}</p>
                </div>
                <div className="p-4 rounded-lg bg-surface">
                  <p className="pd-eyebrow mb-1">Pronouns</p>
                  <p className="text-[15px] font-medium text-ink">{user?.pronouns || 'Not set'}</p>
                </div>
                <div className="p-4 rounded-lg bg-surface">
                  <p className="pd-eyebrow mb-1">Previous names</p>
                  <p className="text-[15px] font-medium text-ink">
                    {user?.previous_names?.length > 0 ? user.previous_names.join(', ') : 'None added'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-surface sm:col-span-2">
                  <p className="pd-eyebrow mb-1">Current address</p>
                  <p className="text-[15px] font-medium text-ink">
                    {user?.current_address
                      ? `${user.current_address.address_line1}, ${user.current_address.city}, ${user.current_address.postcode}`
                      : 'Not set'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ID Documents */}
        <Card className="bg-ground mb-4">
          <CardHeader>
            <CardTitle className="font-heading text-[19px] font-semibold tracking-[-0.02em]">Identity documents</CardTitle>
            <CardDescription className="text-ink-55">
              Organisations usually ask you to prove who you are before they reply to a request. Upload your ID once here.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Permission checkbox */}
            <div className={`flex items-start gap-3 p-4 rounded-lg ${
              idSharingPermission ? 'bg-brand-wash' : 'bg-amber-wash'
            }`}>
              <Checkbox
                id="id-permission"
                checked={idSharingPermission}
                onCheckedChange={handleSaveIdPermission}
                disabled={reencrypting}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label
                    htmlFor="id-permission"
                    className={`text-[15px] font-semibold cursor-pointer ${
                      idSharingPermission ? 'text-brand-ink' : 'text-amber-ink'
                    }`}
                  >
                    Send my ID automatically when an organisation asks for it
                  </Label>
                  {reencrypting && (
                    <Loader2 className="w-4 h-4 animate-spin text-ink-55" aria-label="Updating" />
                  )}
                </div>

                {idSharingPermission ? (
                  <div className="mt-2 space-y-2 text-sm text-brand-ink">
                    <p>
                      On. When an organisation replies asking for ID, your stored documents
                      are sent for you. Nothing needs you.
                    </p>
                    <p className="text-brand-ink/80">
                      To do this, your ID documents are stored so the system can open them.
                      Turning this off re-encrypts them so only your key can.
                    </p>
                  </div>
                ) : (
                  <div className="mt-2 space-y-2 text-sm text-amber-ink">
                    <p>
                      Off. Only your key can open your ID documents. When an organisation asks
                      for ID, we tell you and you approve sending it each time.
                    </p>
                    <p className="text-amber-ink/80">
                      Turn this on if you would rather we handle it. You can turn it off at any time.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload sections */}
            <div className="grid sm:grid-cols-2 gap-3">
              {/* Proof of ID */}
              <div className="p-4 rounded-lg bg-surface">
                <h4 className="text-[15px] font-semibold text-ink mb-1">Proof of who you are</h4>
                <p className="text-sm text-ink-55 mb-4">
                  A passport, driving licence or national ID card.
                </p>

                {proofOfId ? (
                  idDocRow(proofOfId, 'proof-of-id')
                ) : (
                  <>
                    <input
                      type="file"
                      id="proof-id-upload"
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={(e) => handleIdFileSelect(e, 'proof_of_id')}
                    />
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => document.getElementById('proof-id-upload').click()}
                      disabled={uploadingId}
                    >
                      {uploadingId && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Upload a document
                    </Button>
                  </>
                )}
              </div>

              {/* Proof of Address */}
              <div className="p-4 rounded-lg bg-surface">
                <h4 className="text-[15px] font-semibold text-ink mb-1">Proof of where you live</h4>
                <p className="text-sm text-ink-55 mb-4">
                  A utility bill, bank statement or council tax bill from the last three months.
                </p>

                {proofOfAddress ? (
                  idDocRow(proofOfAddress, 'proof-of-address')
                ) : (
                  <>
                    <input
                      type="file"
                      id="proof-address-upload"
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={(e) => handleIdFileSelect(e, 'proof_of_address')}
                    />
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => document.getElementById('proof-address-upload').click()}
                      disabled={uploadingId}
                    >
                      {uploadingId && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Upload a document
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Name Change Evidence - only shown if user has previous names */}
            {user?.previous_names?.length > 0 && (
              <div className="p-4 rounded-lg bg-surface">
                <h4 className="text-[15px] font-semibold text-ink mb-1">Proof of a name change</h4>
                <p className="text-sm text-ink-55 mb-1">
                  You have previous names on record, so some organisations may ask for proof.
                  You can upload more than one document.
                </p>
                <p className="text-sm text-ink-55 mb-4">
                  A deed poll, marriage certificate, adoption order, special guardianship order or divorce decree.
                </p>

                {/* List of uploaded name change documents */}
                {nameChangeDocs.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {nameChangeDocs.map((doc) => idDocRow(doc, 'name-change'))}
                  </div>
                )}

                {/* Upload button - always visible */}
                <input
                  type="file"
                  id="name-change-upload"
                  className="hidden"
                  accept="image/*,.pdf"
                  onChange={(e) => handleIdFileSelect(e, 'name_change_evidence')}
                />
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => document.getElementById('name-change-upload').click()}
                  disabled={uploadingId}
                >
                  {uploadingId && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {nameChangeDocs.length > 0 ? 'Upload another document' : 'Upload a document'}
                </Button>
              </div>
            )}

            <p className="text-sm text-ink-55">
              Your ID documents are encrypted. They are only shared with an organisation if you allow it above, or approve it yourself.
            </p>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
        {/* Security */}
        <Card className="bg-ground mb-4">
          <CardHeader>
            <CardTitle className="font-heading text-[19px] font-semibold tracking-[-0.02em]">Security</CardTitle>
            <CardDescription className="text-ink-55">
              Two-factor authentication protects your account. A recovery code means a forgotten password never loses your records.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-surface">
              <div>
                <h4 className="text-[15px] font-semibold text-ink mb-1.5">Two-factor authentication</h4>
                {user?.mfa_enabled ? (
                  <StatusPill tone="done" size="sm">On</StatusPill>
                ) : (
                  <StatusPill tone="needs" size="sm">Not set up</StatusPill>
                )}
              </div>
              {!user?.mfa_enabled && (
                <Button
                  variant="secondary"
                  onClick={() => navigate('/setup-mfa', { state: { from: 'profile' } })}
                  data-testid="enable-mfa-profile-btn"
                >
                  Turn on two-factor
                </Button>
              )}
            </div>

            {/* Recovery code */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-surface" data-testid="recovery-code-row">
              <div>
                <h4 className="text-[15px] font-semibold text-ink mb-1.5">Recovery code</h4>
                {user?.has_recovery_code ? (
                  <StatusPill tone="done" size="sm">
                    Set up{user?.recovery_code_created_at ? ` · ${new Date(user.recovery_code_created_at).toLocaleDateString('en-GB')}` : ''}
                  </StatusPill>
                ) : (
                  <div className="space-y-1">
                    <StatusPill tone="needs" size="sm">Not set up</StatusPill>
                    <p className="text-sm text-amber-ink">Without one, a forgotten password loses your records.</p>
                  </div>
                )}
              </div>
              <Button
                variant="secondary"
                onClick={() => { setRecoveryError(''); setShowRecoveryPrompt(true); }}
                data-testid="generate-recovery-code-btn"
              >
                {user?.has_recovery_code ? 'Generate a new code' : 'Create a recovery code'}
              </Button>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="account" className="space-y-4">
        {/* Data Management */}
        <Card className="bg-ground mb-4">
          <CardHeader>
            <CardTitle className="font-heading text-[19px] font-semibold tracking-[-0.02em]">Your data</CardTitle>
            <CardDescription className="text-ink-55">
              Take a copy of everything, or delete your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-surface">
              <div>
                <h4 className="text-[15px] font-semibold text-ink">Export everything</h4>
                <p className="text-sm text-ink-55">Download a copy of all your data.</p>
              </div>
              <Button
                variant="secondary"
                onClick={handleExportData}
                disabled={exporting}
                data-testid="export-data-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Preparing export" /> : 'Export'}
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-surface">
              <div>
                <h4 className="text-[15px] font-semibold text-ink">Delete your account</h4>
                <p className="text-sm text-ink-55">Permanently removes your account and everything in it.</p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                data-testid="delete-account-btn"
              >
                Delete account
              </Button>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="privacy" className="space-y-4">
        {/* Privacy */}
        <Card className="bg-ground mb-4">
          <CardHeader>
            <CardTitle className="font-heading text-[19px] font-semibold tracking-[-0.02em]">Privacy</CardTitle>
            <CardDescription className="text-ink-55">
              Help us hold organisations to account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`flex items-start gap-3 p-4 rounded-lg ${contributeStats ? 'bg-brand-wash' : 'bg-surface'}`}>
              <Checkbox
                id="contribute-stats"
                checked={contributeStats}
                onCheckedChange={handleToggleContributeStats}
                disabled={savingContributeStats}
                className="mt-1"
                data-testid="contribute-stats-checkbox"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label
                    htmlFor="contribute-stats"
                    className={`text-[15px] font-semibold cursor-pointer ${contributeStats ? 'text-brand-ink' : 'text-ink'}`}
                  >
                    Contribute anonymised stats
                  </Label>
                  {savingContributeStats && (
                    <Loader2 className="w-4 h-4 animate-spin text-ink-55" aria-label="Updating" />
                  )}
                </div>
                <div className={`mt-2 space-y-1.5 text-sm ${contributeStats ? 'text-brand-ink' : 'text-ink-55'}`}>
                  <p>
                    When your documents are analysed, we can add anonymised counts — how
                    much was redacted, how much we were able to recover, whether a
                    redaction looks disproportionate or unlawful — to that organisation's
                    overall record. We use this to build a picture of how councils and
                    other organisations handle care leavers' data.
                  </p>
                  <p className={contributeStats ? 'text-brand-ink/80' : 'text-ink-55'}>
                    Only numbers are ever shared — never your text, your records, or
                    anything that identifies you. An organisation's figures only appear
                    once several people have contributed, so no single person's request
                    can ever be picked out. On by default; off anytime, right here.
                  </p>
                </div>
              </div>
            </div>

            {/* Demographic contribution — a second, separate opt-in. Never
                bundled with the toggle above: ethnicity is special category
                data under UK GDPR, so this needs its own explicit consent,
                unchecked by default. */}
            <div className={`mt-4 flex items-start gap-3 p-4 rounded-lg ${contributeDemographics ? 'bg-brand-wash' : 'bg-surface'}`}>
              <Checkbox
                id="contribute-demographics"
                checked={contributeDemographics}
                onCheckedChange={handleToggleContributeDemographics}
                disabled={savingContributeDemographics}
                className="mt-1"
                data-testid="contribute-demographics-checkbox"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label
                    htmlFor="contribute-demographics"
                    className={`text-[15px] font-semibold cursor-pointer ${contributeDemographics ? 'text-brand-ink' : 'text-ink'}`}
                  >
                    Also include my ethnicity, gender and age range
                  </Label>
                  {savingContributeDemographics && (
                    <Loader2 className="w-4 h-4 animate-spin text-ink-55" aria-label="Updating" />
                  )}
                </div>
                <p className={`mt-2 text-sm ${contributeDemographics ? 'text-brand-ink' : 'text-ink-55'}`}>
                  Off by default — a separate choice from the setting above. If you turn this
                  on, the stats you contribute are also broken down by ethnicity, gender (using
                  what you've set above under Personal information) and a broad age range
                  (derived from your date of birth, never the exact date), so we can see
                  whether organisations treat people differently by background. A category is
                  only ever shown once enough people have answered it — never for a small
                  number of people. Choose your ethnicity and disability below; leave
                  any blank to skip it.
                </p>
                {contributeDemographics && (
                  <div className="grid sm:grid-cols-3 gap-3 mt-3" data-testid="demographics-fields">
                    <div>
                      <Label className="text-xs text-brand-ink/80 mb-1 block">Gender</Label>
                      <p className="text-sm text-brand-ink py-2" data-testid="demographics-gender-readonly">
                        {user?.gender || 'Not set'}
                      </p>
                      <button
                        type="button"
                        onClick={() => setActiveTab('personal')}
                        className="text-xs text-brand-ink underline underline-offset-2 hover:no-underline"
                        data-testid="demographics-gender-edit-link"
                      >
                        Change on Personal tab
                      </button>
                    </div>
                    <div>
                      <Label className="text-xs text-brand-ink/80 mb-1 block">Ethnicity</Label>
                      <Select
                        value={demographicsForm.ethnicity || undefined}
                        onValueChange={(v) => handleDemographicFieldChange('ethnicity', v)}
                        disabled={savingDemographicField === 'ethnicity'}
                      >
                        <SelectTrigger data-testid="demographics-ethnicity-trigger">
                          <SelectValue placeholder="Not answered" />
                        </SelectTrigger>
                        <SelectContent>
                          {demographicsOptions.ethnicity.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-brand-ink/80 mb-1 block">Disability</Label>
                      <Select
                        value={demographicsForm.disability || undefined}
                        onValueChange={(v) => handleDemographicFieldChange('disability', v)}
                        disabled={savingDemographicField === 'disability'}
                      >
                        <SelectTrigger data-testid="demographics-disability-trigger">
                          <SelectValue placeholder="Not answered" />
                        </SelectTrigger>
                        <SelectContent>
                          {demographicsOptions.disability.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="account" className="space-y-4">
        {/* Shared access */}
        <div className="mb-4">
          <SharedAccessCard />
        </div>

        {/* Activity (collapsed by default) */}
        <div className="mb-4">
          <ProfileActivityCard />
        </div>
          </TabsContent>
        </Tabs>

        {/* Sign Out */}
        <div className="mt-8 pt-8 border-t border-rule">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full sm:w-auto"
            data-testid="profile-logout-btn"
          >
            Sign out
          </Button>
        </div>
      </main>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
              Delete your account?
            </DialogTitle>
            <DialogDescription className="text-ink-55">
              This cannot be undone. It permanently deletes:
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-ink-70 space-y-1 list-disc list-inside">
            <li>Your personal details</li>
            <li>Every organisation and your care history</li>
            <li>Every request you have sent</li>
            <li>Every document, including the encrypted files</li>
          </ul>
          <div className="space-y-2">
            <Label htmlFor="delete-confirm" className="text-[15px] font-semibold">Type DELETE to confirm</Label>
            <Input
              id="delete-confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Deleting" /> : 'Delete account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avatar Cropper Dialog */}
      <AvatarCropper
        open={showAvatarCropper}
        onOpenChange={setShowAvatarCropper}
        onSave={handleAvatarSave}
        currentAvatar={user?.avatar_url}
      />

      {/* Recovery code: confirm password, then show the one-time code */}
      <Dialog open={showRecoveryPrompt} onOpenChange={(next) => { if (!recoveryBusy) { setShowRecoveryPrompt(next); if (!next) { setRecoveryPassword(''); setRecoveryError(''); } } }}>
        <DialogContent data-testid="recovery-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
              {user?.has_recovery_code ? 'Generate a new recovery code' : 'Create your recovery code'}
            </DialogTitle>
            <DialogDescription className="text-ink-55">
              {user?.has_recovery_code
                ? 'Your current code stops working as soon as the new one is created. Confirm your password to continue.'
                : 'A recovery code lets you reset a forgotten password without losing your encrypted records. Confirm your password to create one.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerateRecoveryCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recovery-password" className="text-[15px] font-semibold">What is your password?</Label>
              <Input
                id="recovery-password"
                type="password"
                value={recoveryPassword}
                onChange={(e) => { setRecoveryPassword(e.target.value); if (recoveryError) setRecoveryError(''); }}
                placeholder="Your password"
                autoFocus
                autoComplete="current-password"
                data-testid="recovery-password-input"
              />
              {recoveryError && <p className="text-sm text-breach-ink" data-testid="recovery-password-error">{recoveryError}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" disabled={recoveryBusy}
                      onClick={() => setShowRecoveryPrompt(false)}>
                Cancel
              </Button>
              <Button type="submit"
                      disabled={!recoveryPassword || recoveryBusy} data-testid="recovery-password-submit">
                {recoveryBusy ? 'Creating' : 'Create code'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <RecoveryCodeDialog
        open={Boolean(issuedRecoveryCode)}
        code={issuedRecoveryCode}
        onDone={async () => { setIssuedRecoveryCode(null); await loadUser(); }}
      />

      {/* Password Unlock Dialog (re-derives private key when sessionStorage was cleared) */}
      <PasswordUnlockDialog
        open={showUnlockDialog}
        onOpenChange={(next) => {
          setShowUnlockDialog(next);
          if (!next) {
            setPendingIdPermission(null);
            setPendingExport(false);
          }
        }}
        onUnlocked={handleUnlocked}
        reason={
          pendingExport
            ? 'To include your documents in the export, confirm your password to unlock them.'
            : 'To turn on automatic ID sharing, your ID documents need to be re-encrypted. Confirm your password to unlock them.'
        }
      />
    </AppShell>
  );
};


// =========================================================================
// Activity card (shown on the profile page) — plain English timeline
// =========================================================================
const FRIENDLY_VERBS = {
  account_created: 'Account created',
  login: 'You signed in',
  logout: 'You signed out',
  password_changed: 'You changed your password',
  mfa_enabled: 'You enabled two-factor authentication',
  mfa_disabled: 'You disabled two-factor authentication',
  profile_updated: 'You updated your profile',
  document_uploaded: 'You uploaded a document',
  document_viewed: 'You viewed a document',
  document_analysis_started: 'We started reading a document',
  document_analysis_completed: 'We finished reading a document',
  document_analysis_failed: 'A document could not be read',
  sar_created: 'You created a request',
  sar_sent: 'You sent a request',
  sar_reply_sent: 'You replied to an organisation',
  sar_status_changed: 'A request changed status',
  organisation_added: 'You added an organisation',
  organisation_edited: 'You edited an organisation',
  organisation_removed: 'You removed an organisation',
  document_decrypted: 'You opened a document',
  document_names_decrypted: 'You opened your list of documents',
  documents_exported: 'You downloaded documents as a ZIP',
  id_document_decrypted: 'You opened an ID document',
  id_document_names_decrypted: 'You opened your list of ID documents',
  account_exported: 'You downloaded a copy of everything in your account',
  account_locked: 'Your account was locked after too many failed sign-ins',
  account_unlocked: 'Your account was unlocked',
  recovery_code_generated: 'You made a new recovery code',
  password_recovered: 'You reset your password with your recovery code',
  invitation_created: 'You shared your account with someone',
  invitation_revoked: 'You stopped sharing your account with someone',
};

// The same things, said about someone you shared your account with.
const THIRD_PERSON = {
  document_uploaded: 'uploaded a document',
  document_viewed: 'viewed a document',
  document_decrypted: 'opened a document',
  documents_exported: 'downloaded documents as a ZIP',
  sar_created: 'created a request',
  sar_sent: 'sent a request',
  sar_reply_sent: 'replied to an organisation',
  organisation_added: 'added an organisation',
  organisation_edited: 'edited an organisation',
  organisation_removed: 'removed an organisation',
  profile_updated: 'updated your profile',
  shared_account_accessed: 'opened your account',
};

/** Plain-English line for one activity entry. Exported for tests. */
export const describeActivity = (a) => {
  if (a.action === 'invitee_action') return a.label;
  if (a.by) return THIRD_PERSON[a.action] ? `${a.by} ${THIRD_PERSON[a.action]}` : `${a.by}: ${a.label}`;
  if (a.action === 'login' && a.metadata?.success === false) {
    return 'Someone tried to sign in to your account and it failed';
  }
  return FRIENDLY_VERBS[a.action] || a.label;
};

const ProfileActivityCard = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  // Lazy-load on first expand to keep the profile page snappy.
  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      setLoading(true);
      profileApi
        .myActivity(50)
        .then((r) => {
          setActivities(r.data?.activities || []);
          setLoaded(true);
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }
  };

  return (
    <Card className="bg-ground" data-testid="profile-activity-card">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-inset transition-colors duration-state rounded-lg"
        data-testid="profile-activity-toggle"
        aria-expanded={open}
      >
        <div className="space-y-1.5 min-w-0">
          <p className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">
            Activity
          </p>
          <p className="text-sm text-ink-55">
            A plain record of what has happened in your account, including anything done by
            someone you've shared it with.
          </p>
        </div>
        <ChevronDown
          aria-hidden="true"
          className={`w-5 h-5 text-ink-55 flex-shrink-0 transition-transform duration-state ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <CardContent className="pt-0">
          {loading ? (
            <p className="text-sm text-ink-55">Loading</p>
          ) : error ? (
            <p className="text-sm text-breach-ink">Your activity could not be loaded right now.</p>
          ) : activities.length === 0 ? (
            <p className="text-sm text-ink-55">Nothing here yet. Activity appears as you use your account.</p>
          ) : (
            <ul className="space-y-3" data-testid="profile-activity-list">
              {activities.map((a) => (
                <li key={a.id} className="flex items-start gap-3 text-sm">
                  <span aria-hidden="true" className="w-[7px] h-[7px] rounded-full bg-shape mt-2 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-ink">
                      {describeActivity(a)}
                      {a.count > 1 ? ` · ${a.count} times` : ''}
                    </p>
                    <p className="pd-mono text-ink-55 mt-0.5">
                      {a.timestamp ? new Date(a.timestamp).toLocaleString(undefined, {
                        day: 'numeric', month: 'long', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      }) : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
};


export default Profile;